import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
	IconArrowLeft,
	IconArrowRight,
	IconHeartFilled,
	IconRefresh,
	IconSend,
	IconSparkles,
} from "@tabler/icons-react";
import { useApi } from "../hooks/useApi.ts";
import { api, type TodayQuiz, type QuizQuestion } from "../lib/api.ts";
import {
	Card,
	ErrorBlock,
	PageSkeleton,
	Pill,
	EmptyState,
} from "../components/Ui.tsx";
import { categoryDisplayName } from "../lib/quiz-content.ts";
import { formatQuizDate } from "../lib/date.ts";
import { toast } from "../lib/toast.tsx";
import { FunLoader } from "../components/FunLoader.tsx";
import { AnimatedEmoji } from "../components/AnimatedEmoji.tsx";

type Opt = "A" | "B" | "C" | "D";

const CONFIRM_MS = 600;
const FADE_MS = 250;
const SLOW_THRESHOLD_MS = 6_000;

export default function Quiz() {
	const { getToken } = useAuth();
	const { data, loading, error, reload } = useApi<TodayQuiz>((t) =>
		api.todayQuiz(t),
	);
	const navigate = useNavigate();

	const [userStep, setUserStep] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [pendingOpt, setPendingOpt] = useState<Opt | null>(null);
	const [transitioning, setTransitioning] = useState(false);
	const [slow, setSlow] = useState(false);
	const [sessionPicks, setSessionPicks] = useState<Record<string, Opt>>({});
	const [hiddenIds, setHiddenIds] = useState<Record<string, true>>({});
	const timersRef = useRef<Set<ReturnType<typeof window.setTimeout>>>(new Set());
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		const timers = timersRef.current;
		return () => {
			mountedRef.current = false;
			timers.forEach((id) => clearTimeout(id));
			timers.clear();
		};
	}, []);

	const scheduleTimer = useCallback(
		(cb: () => void, ms: number) => {
			const id = setTimeout(() => {
				timersRef.current.delete(id);
				if (mountedRef.current) cb();
			}, ms);
			timersRef.current.add(id);
			return id;
		},
		[],
	);

	const total = data?.questions.length ?? 0;
	const myAnswers = useMemo(
		() => data?.myAnswerByQuestion ?? {},
		[data],
	);
	const firstUnansweredIdx = useMemo(() => {
		if (!data) return 0;
		return data.questions.findIndex((q) => !myAnswers[q.id]);
	}, [data, myAnswers]);
	const targetStep = firstUnansweredIdx >= 0 ? firstUnansweredIdx : 0;
	const currentStep = userStep !== null ? userStep : targetStep;
	const current = total > 0 ? data?.questions[currentStep] : undefined;
	const isFirst = currentStep === 0;
	const isLast = currentStep === total - 1;
	const allMineAnswered =
		!!data && data.questions.every((q) => Boolean(myAnswers[q.id]));
	const savedForCurrent = current
		? (hiddenIds[current.id]
				? undefined
				: (sessionPicks[current.id] ??
					(myAnswers[current.id]?.toUpperCase() as Opt | undefined)))
		: undefined;
	const answeredCount = data?.myAnsweredCount ?? 0;
	const progressPercent =
		total > 0 ? Math.round(((currentStep + 1) / total) * 100) : 0;

	const lockedToFirstUnanswered = userStep === null;

	/* eslint-disable react-hooks/set-state-in-effect */
	useEffect(() => {
		if (!loading) {
			setSlow(false);
			return undefined;
		}
		const id = window.setTimeout(
			() => setSlow(true),
			SLOW_THRESHOLD_MS,
		);
		return () => window.clearTimeout(id);
	}, [loading]);
	/* eslint-enable react-hooks/set-state-in-effect */

	function goToStep(next: number) {
		if (next < 0 || next >= total) return;
		setPendingOpt(null);
		const targetQ = data?.questions[next];
		if (targetQ) {
			setHiddenIds((prev) => ({ ...prev, [targetQ.id]: true }));
		setSessionPicks((prev) => {
			if (!(targetQ.id in prev)) return prev;
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { [targetQ.id]: _, ...rest } = prev;
			return rest;
		});
		}
		setTransitioning(true);
		scheduleTimer(() => {
			setUserStep(next);
			setTransitioning(false);
		}, FADE_MS);
	}

	async function persistAnswer(q: QuizQuestion, opt: Opt): Promise<boolean | null> {
		setSubmitting(true);
		setSubmitError(null);
		try {
			const token = await getToken();
			if (!token) throw new Error("Tidak ada token");
			const res = await api.answer(token, {
				questionId: q.id,
				option: opt,
			});
			if (res.bothAnswered) {
				toast.success("Semua jawaban tersimpan!", {
					description: "Kamu dan pasangan sudah selesai. Lihat hasilnya!",
				});
			}
			reload();
			return res.bothAnswered;
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e);
			setSubmitError(msg);
			toast.error("Gagal menyimpan jawaban", { description: msg });
			return null;
		} finally {
			setSubmitting(false);
		}
	}

	async function handleSelect(opt: Opt) {
		if (submitting || !current || transitioning) return;
		if (pendingOpt) return;
		setUserStep(currentStep);
		setPendingOpt(opt);
		setHiddenIds((prev) => {
			if (!(current.id in prev)) return prev;
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { [current.id]: _, ...rest } = prev;
			return rest;
		});
		setSessionPicks((prev) => ({ ...prev, [current.id]: opt }));
		if (isLast) {
			setPendingOpt(null);
			return;
		}
		const bothDone = await persistAnswer(current, opt);
		if (bothDone === null) {
			setPendingOpt(null);
			return;
		}
		if (bothDone) {
			setPendingOpt(null);
			return;
		}
		scheduleTimer(() => {
			setPendingOpt(null);
			const nextIdx = data?.questions.findIndex((q, i) => i > currentStep && !myAnswers[q.id]);
			if (nextIdx !== undefined && nextIdx >= 0) {
				setUserStep(nextIdx);
			} else {
				const next = currentStep + 1;
				if (next < total) {
					setUserStep(next);
				}
			}
		}, CONFIRM_MS);
	}

	function handleBack() {
		if (isFirst || submitting || transitioning) return;
		goToStep(currentStep - 1);
	}

	if (loading) {
		return (
			<>
				{slow ? (
					<FunLoader variant="quiz" />
				) : (
					<PageSkeleton variant="quiz" />
				)}
				{slow && (
					<div style={{ marginTop: "1rem", textAlign: "center" }}>
						<button
							type="button"
							className="btn btn-ghost"
							onClick={reload}
						>
							<IconRefresh size={14} /> Coba lagi
						</button>
					</div>
				)}
			</>
		);
	}
	if (error) return <ErrorBlock message={error} />;
	if (!data) return <ErrorBlock message="Tidak ada data" />;

	if (data.questions.length === 0) {
		return (
			<EmptyState
				icon={<IconSparkles size={28} />}
				title="Belum ada kuis hari ini"
				description="Kuis baru dibuat setiap 00:00 WIB. Coba lagi beberapa saat."
				action={
					<button
						type="button"
						className="btn btn-primary"
						onClick={reload}
					>
						<IconRefresh size={14} /> Refresh
					</button>
				}
			/>
		);
	}

	if (data.bothAnswered || allMineAnswered) {
		return (
			<div style={{ display: "grid", gap: "1rem" }}>
				<Card style={{ textAlign: "center", padding: "2rem 1rem" }}>
					<AnimatedEmoji name="selesai" size={64} style={{ marginInline: "auto" }} />
					<h2 style={{ margin: "0.75rem 0 0.35rem", fontSize: "1.15rem" }}>
						Kamu sudah selesai!
					</h2>
					<p className="muted" style={{ margin: "0 0 1.25rem" }}>
						{data.bothAnswered
							? "Bandingkan jawabanmu dengan pasangan."
							: "Tunggu pasanganmu juga menyelesaikan kuis."}
					</p>
					<button
						className="btn btn-primary"
						style={{ width: "100%" }}
						onClick={() => navigate("/result")}
					>
						{data.bothAnswered ? "Lihat hasil" : "Lihat status"}{" "}
						<IconArrowRight size={16} />
					</button>
				</Card>
			</div>
		);
	}

	return (
		<div style={{ display: "grid", gap: "1rem" }}>
			<Card>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<div>
						<Pill>
							<IconHeartFilled size={12} /> Kategori:{" "}
							{categoryDisplayName(data.category)}
						</Pill>
						<h2 style={{ margin: "0.4rem 0 0.15rem" }}>Kuis hari ini</h2>
						<p className="muted" style={{ margin: 0 }}>
							{formatQuizDate(data.date)}
						</p>
					</div>
					<Pill tone="info">
						{currentStep + 1}/{total}
					</Pill>
				</div>
				<div
					style={{
						marginTop: "0.6rem",
						height: 6,
						background: "var(--color-surface-2)",
						borderRadius: 999,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							height: "100%",
							width: `${progressPercent}%`,
							background: "var(--color-primary)",
							transition: "width 200ms ease",
						}}
					/>
				</div>
				<p
					className="muted"
					style={{ margin: "0.4rem 0 0", fontSize: "0.78rem" }}
				>
					{answeredCount} dari {total} terjawab
					{lockedToFirstUnanswered ? "" : " · melihat jawaban sebelumnya"}
				</p>
			</Card>

			{submitError && <ErrorBlock message={submitError} />}

			{current && (
				<>
				<div key={current.id}>
						<QuestionCard
							question={current}
							index={currentStep + 1}
							selected={savedForCurrent ?? null}
							pendingOpt={pendingOpt}
							onSelect={handleSelect}
							disabled={submitting || transitioning || Boolean(pendingOpt)}
						/>
					</div>

					{!isFirst && (
						<button
							className="btn btn-secondary w-full"
							onClick={handleBack}
							disabled={submitting || transitioning}
						>
							<IconArrowLeft size={16} /> Kembali
						</button>
					)}

					{isLast && savedForCurrent && (
						<button
							className="btn btn-primary w-full"
							onClick={() => {
								if (!current || submitting) return;
								persistAnswer(current, savedForCurrent);
							}}
							disabled={submitting || transitioning}
						>
							{submitting ? (
								<>
									<span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
									Mengirim…
								</>
							) : (
								<><IconSend size={16} /> Kirim</>
							)}
						</button>
					)}
				</>
			)}
		</div>
	);
}

function QuestionCard({
	question,
	index,
	selected,
	pendingOpt,
	onSelect,
	disabled,
}: {
	question: QuizQuestion;
	index: number;
	selected: Opt | null;
	pendingOpt: Opt | null;
	onSelect: (opt: Opt) => void;
	disabled: boolean;
}) {
	const options = [
		{ k: "A" as const, v: question.optionA },
		{ k: "B" as const, v: question.optionB },
		{ k: "C" as const, v: question.optionC },
		{ k: "D" as const, v: question.optionD },
	];
	return (
		<Card>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginBottom: "0.55rem",
				}}
			>
				<span
					aria-label={`Pertanyaan ${index}`}
					style={{
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						width: 40,
						height: 40,
						borderRadius: "var(--radius-full)",
						background: "var(--color-primary-soft)",
						color: "var(--color-primary)",
						border: "2px solid var(--color-border)",
						fontSize: "1rem",
						fontWeight: 800,
						letterSpacing: "0.04em",
						lineHeight: 1,
					}}
				>
					{index}
				</span>
			</div>
			<h3
				style={{
					margin: "0 0 0.95rem",
					textAlign: "center",
					fontSize: "1rem",
					fontWeight: 600,
					lineHeight: 1.45,
				}}
			>
				{question.questionText}
			</h3>
			<div
				style={{
					display: "grid",
					gap: "0.5rem",
				}}
			>
				{options.map((o) => {
					const isSelected = selected === o.k;
					const isPending = pendingOpt === o.k;
					const animationClass = isPending ? "option-confirm" : "";
					return (
						<button
							key={o.k}
							className={
								isSelected
									? `quiz-option quiz-option-selected ${animationClass}`
									: `quiz-option ${animationClass}`
							}
							style={{
								justifyContent: "flex-start",
								textAlign: "left",
								width: "100%",
								padding: "0.75rem 0.9rem",
								borderRadius: "var(--radius-md)",
								fontSize: "0.9rem",
								fontWeight: 500,
								cursor: disabled ? "not-allowed" : "pointer",
								transition: "background-color 150ms ease, border-color 150ms ease",
							}}
							onClick={() => onSelect(o.k)}
							disabled={disabled}
						>
							<span>{o.v}</span>
						</button>
					);
				})}
			</div>
		</Card>
	);
}
